import { router, useLocalSearchParams } from 'expo-router';
import { useEffect, useState } from 'react';
import { StyleSheet, Text, View } from 'react-native';

import { Button } from '@/src/components/Button';
import { Input } from '@/src/components/Input';
import { Screen } from '@/src/components/Screen';
import { useToast } from '@/src/components/Toast';
import { PhoneVerificationCodeModal } from '@/src/features/verification/PhoneVerificationCodeModal';
import {
  confirmPhoneVerificationCode,
  redeemPhoneVerificationHandoff,
  sendPhoneVerificationCode,
} from '@/src/features/verification/verification-service';
import {
  formatUsPhoneNumberInput,
  normalizeUsPhoneNumber,
} from '@/src/features/verification/phone-number';
import { colors } from '@/src/theme/colors';
import { spacing } from '@/src/theme/spacing';

export default function VerifyPhoneScreen() {
  const params = useLocalSearchParams<{ handoff?: string }>();
  const handoff = Array.isArray(params.handoff)
    ? params.handoff[0]
    : params.handoff;
  const { showError, showSuccess } = useToast();
  const [phoneNumber, setPhoneNumber] = useState('');
  const [code, setCode] = useState('');
  const [verificationId, setVerificationId] = useState<string | null>(null);
  const [codeModalVisible, setCodeModalVisible] = useState(false);
  const [loading, setLoading] = useState(Boolean(handoff));
  const [handoffReady, setHandoffReady] = useState(!handoff);
  const normalizedPhoneNumber = normalizeUsPhoneNumber(phoneNumber);

  useEffect(() => {
    let active = true;

    if (!handoff) {
      return undefined;
    }

    setLoading(true);
    redeemPhoneVerificationHandoff(handoff)
      .then(() => {
        if (active) {
          setHandoffReady(true);
        }
      })
      .catch((error) => {
        if (active) {
          showError(error, 'Unable to open phone verification.');
        }
      })
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, [handoff, showError]);

  async function handleSendCode() {
    setLoading(true);

    try {
      const result = await sendPhoneVerificationCode(phoneNumber);

      if (result.handoff) {
        showSuccess(
          'Open the browser page to verify your phone.',
          'Phone sent',
        );
        return;
      }

      setVerificationId(result.verificationId);
      setCodeModalVisible(true);
      showSuccess('Enter the SMS code sent to your phone.', 'Code sent');
    } catch (error) {
      showError(error, 'Unable to send verification code.');
    } finally {
      setLoading(false);
    }
  }

  function handlePhoneNumberChange(value: string) {
    setPhoneNumber(formatUsPhoneNumberInput(value));
    setCode('');
    setVerificationId(null);
    setCodeModalVisible(false);
  }

  async function handleConfirmCode() {
    if (!verificationId) {
      return;
    }

    setLoading(true);

    try {
      await confirmPhoneVerificationCode({ code, verificationId });
      setCodeModalVisible(false);
      showSuccess('Your phone number is verified.', 'Phone verified');
      router.replace('/settings');
    } catch (error) {
      showError(error, 'Unable to verify that code.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <Screen
      title="Verify phone"
      subtitle="Add a verified cell phone number to qualify for a verified account badge."
    >
      <View style={styles.panel}>
        <Text style={styles.description}>
          Enter a US cell phone number. Spaces, dashes, parentheses, and +1 are
          all fine. Frienzzi uses Firebase to send the SMS verification code.
        </Text>
        <Input
          autoComplete="tel"
          editable={handoffReady && !loading}
          keyboardType="phone-pad"
          label="Cell phone"
          onChangeText={handlePhoneNumberChange}
          placeholder="(303) 555-0123"
          textContentType="telephoneNumber"
          value={phoneNumber}
        />
        <View
          nativeID="frienzzi-phone-verification-recaptcha"
          style={styles.recaptchaContainer}
        />
        <Button
          disabled={!handoffReady || loading || !normalizedPhoneNumber}
          loading={loading && !verificationId}
          onPress={handleSendCode}
          title="Send code"
        />
        {verificationId ? (
          <Button
            title="Enter verification code"
            variant="secondary"
            onPress={() => setCodeModalVisible(true)}
          />
        ) : null}
      </View>
      <PhoneVerificationCodeModal
        code={code}
        loading={loading}
        visible={Boolean(verificationId) && codeModalVisible}
        onChangeCode={setCode}
        onClose={() => setCodeModalVisible(false)}
        onConfirm={handleConfirmCode}
      />
    </Screen>
  );
}

const styles = StyleSheet.create({
  description: {
    color: colors.mutedText,
    fontSize: 14,
    lineHeight: 20,
  },
  panel: {
    backgroundColor: colors.surface,
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: StyleSheet.hairlineWidth,
    gap: spacing.md,
    padding: spacing.md,
  },
  recaptchaContainer: {
    alignSelf: 'flex-start',
    minHeight: 78,
    minWidth: 304,
  },
});
